import React, { useState } from "react";
import { Box } from "@rebass/grid";
import { styled } from "@mui/system";
import hexToRgba from "hex-to-rgba";

import { SingleSideMenuItem } from "../constants/sideMenuItem";

import themes from "components/themes";

import Icon, { Icons } from "components/Icon";
import SideMenuItem from "./SideMenuItem";

interface SideMenuItemListProps {
  item: SingleSideMenuItem;
  items?: SingleSideMenuItem[];
  currentRoute: string;
  depth: number;
  hideTitle?: boolean;
  onItemClick: (item: SingleSideMenuItem) => void;
  className?: string;
  highlighted?: boolean;
}

const isActive = (item: SingleSideMenuItem, currentRoute: string) => {
  const menuRoutes = [item.route, ...(item.additionalRoutes || [])];

  return menuRoutes.indexOf(currentRoute) !== -1;
};

function SideMenuItemList({
  item,
  items = [],
  currentRoute,
  depth,
  hideTitle = false,
  onItemClick,
  className = "",
  highlighted = false,
}: SideMenuItemListProps) {
  const [isHovered, setHovered] = useState(false);
  const active = isActive(item, currentRoute);

  return (
    <Container
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Expander
        active={active}
        depth={depth}
        highlighted={highlighted}
        className={className}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onItemClick(item);
        }}
      >
        <ItemBox>
          <SideMenuItem
            icon={item.icon}
            route={item.route || ""}
            depth={depth}
            active={active}
          >
            {!hideTitle && item.title}
          </SideMenuItem>
        </ItemBox>

        {!hideTitle && items.length > 0 && (
          <Box mr={24}>
            <Icon icon={Icons.faCaretRight} color="#fff" size="sm" />
          </Box>
        )}

        <Nested active={isHovered}>
          {items.map((nested) => {
            const nestedDepth = depth + 1;

            return (
              <SideMenuItemList
                item={nested}
                items={nested.items}
                currentRoute={currentRoute}
                depth={nestedDepth}
                key={`menu-${nestedDepth}-${nested.title}`}
                onItemClick={() => {
                  onItemClick(nested);
                }}
              />
            );
          })}
        </Nested>
      </Expander>
    </Container>
  );
}

const Container = styled("div")`
  position: relative;
  background-color: ${themes.colors.background};
`;

const Nested = styled("div", {
  shouldForwardProp: (prop: string) => !["active"].includes(prop),
})<{ active: boolean }>`
  position: absolute;
  z-index: ${(props: any) => props.theme.zIndexes.sideMenu};
  left: 100%;
  right: 0;
  top: 0;
  width: ${(props: any) => (props.active ? "230px" : 0)};
  visibility: ${(props: any) => (props.active ? "visible" : "hidden")};
  transition: width 0.4s;
`;

// eslint-disable-next-line
const Expander = styled("div", {
  shouldForwardProp: (prop: string) =>
    !["active", "depth", "highlighted"].includes(prop),
})<{
  active: boolean;
  depth: number;
  highlighted: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  user-select: none;
  cursor: pointer;
  background-color: ${(props) =>
    props.active
      ? hexToRgba(themes.colors.purple, 0.3)
      : props.highlighted
      ? hexToRgba(themes.colors.white, 0.1)
      : "inherit"};
  border-left: ${(props) =>
    props.active && props.depth === 1
      ? `6px solid ${themes.colors.purple}`
      : props.highlighted
      ? `6px solid ${hexToRgba(themes.colors.white, 0.2)}`
      : "inherit"};

  &:hover {
    background-color: ${hexToRgba(themes.colors.purple, 0.5)};
    ${(props) =>
      !props.active &&
      props.highlighted &&
      `border-left: 4px solid transparent;`}
  }
`;

const ItemBox = styled(Box)`
  width: 100%;
  text-align: center;
`;

export default SideMenuItemList;
